/**
 * `/voice/*` — QueVee Voice's Train (`docs/plans/quevee-voice.md` §6).
 *
 * Served by qv-data on the same app as `/sym/*`. A voice is visible only to
 * whoever may train it; everyone else is told it does not exist, so `voices()`
 * is empty for them and nothing here is offered.
 *
 * `api/journal.ts`'s split: reads degrade, writes throw. A dictation or a save
 * that failed silently would leave John believing his read was kept.
 */

import type { ChartSnapshot } from "../lib/chart-snapshot";
import { requestHeaders } from "../lib/user";
import { httpBase } from "./backend";

const BASE = httpBase(
  import.meta.env.VITE_QV_SYM_URL as string | undefined,
  import.meta.env.VITE_QV_SET_URL as string | undefined,
);

export interface Voice {
  key: string;
  name: string;
  group: string;
  /** Whether Train is open to this caller on it (`store.TRAIN_GROUPS`). */
  train?: boolean;
}

export type Condition = "Bullish" | "Neutral" | "Bearish";

/** A file as the store takes it: bare base64 and its type. */
export interface VoiceFile {
  contentType: string;
  data: string;
}

/** The whole screener row, as the grid shows it. */
export interface ScreenerRowCapture {
  screen?: string;
  columns: { field: string; header: string; value: unknown }[];
}

export interface TrainBody {
  origin: "sample" | "post";
  symbol: string;
  interval: string;
  barDate?: string;
  read: string;
  expected: { condition?: Condition; pattern?: string; stop?: number; target?: number };
  chart?: Omit<ChartSnapshot, "imagePng">;
  screener?: ScreenerRowCapture;
  image?: VoiceFile;
  recordings?: VoiceFile[];
}

export interface TrainedCase {
  id: string;
  origin: string;
  symbol: string;
  inputs: { missing?: string[] } | null;
}

async function failure(response: Response): Promise<Error> {
  let detail = "";
  try {
    const body = (await response.json()) as { detail?: unknown };
    if (typeof body.detail === "string") detail = body.detail;
  } catch {
    // not JSON: the status says enough
  }
  return new Error(detail || `failed (${response.status})`);
}

async function post<T>(path: string, body: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { ...(await requestHeaders()), "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as T;
}

/** The voices this account may train; `[]` for everyone else, and on any failure. */
export async function fetchVoices(): Promise<Voice[]> {
  if (!BASE) return [];
  try {
    const response = await fetch(`${BASE}/voice`, { headers: await requestHeaders() });
    if (!response.ok) return [];
    const body = (await response.json()) as { voices?: Voice[] };
    // Only the voices Train is open to: a group admin may see a voice that is
    // still the platform admin's test.
    return (body.voices ?? []).filter((v) => v.train === true);
  } catch {
    return [];
  }
}

/** The text of a dictation, from our own Whisper. Nothing is stored. */
export function transcribe(voice: string, audio: VoiceFile, symbol: string): Promise<{ text: string }> {
  return post(`/voice/${encodeURIComponent(voice)}/transcribe`, { ...audio, symbol });
}

/** One case from the chart on screen. */
export function train(voice: string, body: TrainBody): Promise<TrainedCase> {
  return post(`/voice/${encodeURIComponent(voice)}/train`, body);
}
